import { useMemo, useState } from "react";
import { ArrowLeftIcon, CheckmarkIcon, FileIcon } from "vega-icons";

import { useCreateNrlItems } from "@vega-api/generated/coinvest-deal-opportunity-nrl/coinvest-deal-opportunity-nrl";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { SearchInput } from "@vega-core/components/Form/Controls/Input/SearchInput/SearchInput";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";

import { useDataRoomData } from "../../../hooks/useDataRoomData";

type NrlTrackNewSearchProps = {
  dealId: string;
  dealOpportunityId: string;
  trackedDocumentIds: string[];
  onBack: () => void;
  onTracked: () => void;
};

export const NrlTrackNewSearch = ({
  dealId,
  dealOpportunityId,
  trackedDocumentIds,
  onBack,
  onTracked,
}: NrlTrackNewSearchProps) => {
  const showErrorToast = useCoInvestErrorToast();
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Mounted only when the "Track new" step is open, so the data-room fetch
  // stays lazy.
  const { parsedData, isLoading, isError } = useDataRoomData(dealId);
  const { mutate: createNrlItems, isPending } = useCreateNrlItems();

  const selectableDocuments = useMemo(() => {
    const alreadyTracked = new Set(trackedDocumentIds);
    const query = search.trim().toLowerCase();

    return Object.values(parsedData.nodeMap).filter(
      (node) =>
        node &&
        node.type === "file" &&
        !alreadyTracked.has(node.id) &&
        parsedData.access[node.id]?.[dealOpportunityId] &&
        (!query || node.name.toLowerCase().includes(query)),
    );
  }, [parsedData, trackedDocumentIds, dealOpportunityId, search]);

  const toggleSelected = (documentId: string) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      next.has(documentId) ? next.delete(documentId) : next.add(documentId);

      return next;
    });
  };

  const handleConfirm = () => {
    const documentIds = [...selectedIds];

    // Atomic all-or-nothing batch: one request tracks every selected document.
    createNrlItems(
      { dealOpportunityId, data: { dataRoomDocumentIds: documentIds } },
      {
        onSuccess: () => onTracked(),
        // Nothing was tracked — keep the selection so the GP can retry.
        onError: (error) =>
          showErrorToast(error, {
            defaultMessage: m.coInvestments_nrl_addError(),
          }),
      },
    );
  };

  const selectedCount = selectedIds.size;

  const documentStatusMessage = (() => {
    if (isLoading) return m.common_loading();

    if (isError) return m.coInvestments_nrl_loadDocumentsError();

    if (selectableDocuments.length === 0)
      return m.coInvestments_nrl_noDocuments();

    return null;
  })();

  const documentListContent = documentStatusMessage ? (
    <p className="body-base-regular text-text/secondary py-2">
      {documentStatusMessage}
    </p>
  ) : (
    selectableDocuments.map((node) =>
      node ? (
        <button
          aria-pressed={selectedIds.has(node.id)}
          className={cnTwMerge(
            "flex w-full items-center gap-2 rounded-default px-3 py-2 body-base-regular text-left hover:bg-background/component focus-outline-themed",
            selectedIds.has(node.id) && "bg-background/component",
          )}
          data-testid={`nrl-document-option-${node.id}`}
          key={node.id}
          onClick={() => toggleSelected(node.id)}
          type="button"
        >
          <FileIcon className="size-3.5 shrink-0 text-text/secondary" />
          <span className="flex-1 min-w-0 truncate">{node.name}</span>
          {selectedIds.has(node.id) ? (
            <CheckmarkIcon className="size-4 shrink-0 text-text/positive" />
          ) : null}
        </button>
      ) : null,
    )
  );

  return (
    <div className="flex flex-col gap-2" data-testid="nrl-track-new">
      <div className="flex items-center gap-2">
        <ThemedButton
          ariaLabel={m.coInvestments_nrl_back()}
          dataTestId="nrl-track-new-back"
          icon={<ArrowLeftIcon className="size-4" />}
          onClick={onBack}
          size="small"
          variant="text"
        />
        <SearchInput
          className="grow"
          name="nrl-document-search"
          onChange={(event) => setSearch(event.target.value)}
          placeholder={m.coInvestments_nrl_searchPlaceholder()}
          value={search}
          withBorder
        />
      </div>

      <div className="flex max-h-48 flex-col overflow-y-auto p-1">
        {documentListContent}
      </div>

      <ThemedButton
        dataTestId="nrl-track-new-confirm"
        disabled={selectedCount === 0 || isPending}
        fullWidth
        onClick={handleConfirm}
        size="small"
        variant="primary"
      >
        {m.coInvestments_nrl_trackSelected({ count: selectedCount })}
      </ThemedButton>
    </div>
  );
};
